import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { merchantApi, statsApi, auth, universalScanUrl, agentApi } from '../../services/api';
import { AdminHeader, PrimaryBtn, GhostBtn, EmptyState } from './AdminLayout';
import QrModal from './QrModal';

export default function MerchantList() {
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSearch, setShowSearch] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(null);
  const [editingMerchant, setEditingMerchant] = useState(null); // { id, googleReviewUrl }
  const [savingMerchant, setSavingMerchant] = useState(false);
  const [overview, setOverview] = useState(null);
  const [qr, setQr] = useState(null);
  // null = 后端没开撮合 agent，入口不显示；数字 = 待运营处理的条数
  const [agentTodo, setAgentTodo] = useState(null);
  const searchTimer = useRef(null);
  const navigate = useNavigate();
  const isSuper = auth.isSuperadmin();

  const load = () => {
    setLoading(true);
    merchantApi.list().then(setMerchants).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    statsApi.overview().then(setOverview).catch(() => {});
    // 后端没配 AGENT_MVP 时这个接口不存在，请求失败入口就不出现
    agentApi.status().then((d) => setAgentTodo(d.todo || 0)).catch(() => setAgentTodo(null));
  }, []);

  const handleSearchInput = (val) => {
    setSearchName(val);
    clearTimeout(searchTimer.current);
    if (!val.trim()) { setSearchResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await merchantApi.search(val.trim());
        setSearchResults(results);
      } finally {
        setSearching(false);
      }
    }, 500);
  };

  const handleSaveMerchant = async () => {
    if (!editingMerchant) return;
    setSavingMerchant(true);
    try {
      await merchantApi.update(editingMerchant.id, {
        googleReviewUrl: editingMerchant.googleReviewUrl,
        googleStoreUrl:  editingMerchant.googleStoreUrl,
        tandianBalance:  Number(editingMerchant.tandianBalance) || 0,
        tandianUrgent:   !!editingMerchant.tandianUrgent,
        // 只在这次动过营业时间时才带上：原来每次保存都重新解析一遍，改个余额也会把
        // 「Google 查到的」变成「运营填的」，解析不了的还会让整个保存被拒（独立审查 2026-10-01）
        ...(editingMerchant.hoursTouched ? {
          businessHoursText: editingMerchant.businessHoursText,
          businessHoursSource: editingMerchant.businessHoursSource,
        } : {}),
      });
      setEditingMerchant(null);
      load();
    } catch (err) {
      // 营业时间看不懂时后端会拒绝保存，把原因原样给运营看
      alert(err?.response?.data?.error || '保存失败');
    } finally {
      setSavingMerchant(false);
    }
  };

  const handleAdd = async (store) => {
    setAdding(String(store.store_id));
    try {
      await merchantApi.add({
        externalId: String(store.store_id),
        name: store.store_name,
        phone: store.store_telephone || '',
        address: store.store_address || '',
      });
      load();
      setShowSearch(false);
      setSearchName('');
      setSearchResults([]);
    } finally {
      setAdding(null);
    }
  };

  return (
    <div style={styles.page}>
      <AdminHeader
        title="商家列表"
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            {auth.hasCap('article') && (
              <GhostBtn small onClick={() => navigate('/admin/blogger-posts')}>博主投稿</GhostBtn>
            )}
            {auth.hasCap('tandian') && (
              <GhostBtn small onClick={() => navigate('/admin/bloggers')}>博主管理</GhostBtn>
            )}
            {isSuper && (
              <>
                <GhostBtn small onClick={() => setQr({ url: universalScanUrl(), title: '通用博主码' })}>通用码</GhostBtn>
                <GhostBtn small onClick={() => navigate('/admin/users')}>账号管理</GhostBtn>
                <GhostBtn small onClick={() => navigate('/admin/qbb-accounts')}>券宝宝账号</GhostBtn>
                {agentTodo !== null && (
                  <GhostBtn small onClick={() => navigate('/admin/agent')}>
                    撮合 Agent{agentTodo > 0 ? ` ${agentTodo}` : ''}
                  </GhostBtn>
                )}
                <PrimaryBtn onClick={() => setShowSearch(true)}>添加商家</PrimaryBtn>
              </>
            )}
            <GhostBtn small onClick={() => auth.logout()}>退出</GhostBtn>
          </div>
        }
      />

      <div style={styles.body}>
        <div style={styles.statsStrip} onClick={() => navigate('/admin/dashboard')}>
          <div style={styles.statsItem}>
            <div style={styles.statsNum}>{overview ? overview.totalScans : '-'}</div>
            <div style={styles.statsLabel}>总扫码</div>
          </div>
          <div style={styles.statsItem}>
            <div style={styles.statsNum}>{overview ? overview.todayScans : '-'}</div>
            <div style={styles.statsLabel}>今日扫码</div>
          </div>
          <div style={styles.statsItem}>
            <div style={styles.statsNum}>{overview ? overview.pendingArticleCount : '-'}</div>
            <div style={styles.statsLabel}>未使用文章</div>
          </div>
          <span style={styles.statsLink}>查看统计</span>
        </div>

        {loading ? (
          <div style={styles.loading}>
            {[1, 2, 3].map((i) => <div key={i} style={styles.skeleton} />)}
          </div>
        ) : merchants.length === 0 ? (
          <EmptyState text={isSuper ? '点击右上角添加商家' : '暂无分配给你的商家，请联系管理员'} />
        ) : (
          <div style={styles.list}>
            {[...merchants].sort((a, b) => (b.tandianUrgent ? 1 : 0) - (a.tandianUrgent ? 1 : 0)).map((m) => (
              <div
                key={m._id}
                style={styles.card}
                onClick={() => navigate(`/admin/merchant/${m._id}`, { state: { merchantName: m.name } })}
              >
                <div style={styles.avatar}>
                  {m.name?.charAt(0) || '?'}
                </div>
                <div style={styles.info}>
                  <div style={styles.name}>{m.tandianUrgent && <span style={styles.urgentTag}>急</span>}{m.name}</div>
                  <div style={styles.idTag}>ID {m.externalId}</div>
                  {(m.phone || m.address) && (
                    <div style={styles.sub}>{m.phone}{m.phone && m.address ? ' · ' : ''}{m.address}</div>
                  )}
                  <div style={styles.scanRow}>累计扫码 <span style={styles.scanNum}>{m.totalScans ?? 0}</span> 次</div>
                  <div style={styles.googleRow}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill={m.googleReviewUrl ? '#4285f4' : '#d1d5db'}>
                      <path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.345-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z"/>
                    </svg>
                    <span style={{ fontSize: 11, color: (m.googleReviewUrl || m.googleStoreUrl) ? '#4285f4' : '#9ca3af' }}>
                      {m.googleReviewUrl ? 'Review 链接已设置' : m.googleStoreUrl ? '店铺链接已设置' : '未设置 Google 链接'}
                    </span>
                  </div>
                </div>
                {isSuper && (
                  <button
                    style={styles.editBtn}
                    onClick={(e) => { e.stopPropagation(); setEditingMerchant({ id: m._id, googleReviewUrl: m.googleReviewUrl || '', googleStoreUrl: m.googleStoreUrl || '', tandianBalance: m.tandianBalance ?? 0, tandianUrgent: !!m.tandianUrgent, businessHoursText: m.businessHoursText || '', businessHoursSource: m.businessHoursSource || '' }); }}
                  >
                    <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-8.5 8.5-3.414.586.586-3.414 8.5-8.5z" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {editingMerchant && (
        <div style={styles.overlay} onClick={() => setEditingMerchant(null)}>
          <div style={styles.editModal} onClick={(e) => e.stopPropagation()}>
            <p style={styles.editModalTitle}>商家设置</p>
            <p style={styles.editModalLabel}>店铺链接（必填，分享链接即可）</p>
            <input
              style={styles.editModalInput}
              value={editingMerchant.googleStoreUrl}
              onChange={(e) => setEditingMerchant(u => ({ ...u, googleStoreUrl: e.target.value }))}
              placeholder="maps.app.goo.gl/xxx"
              autoFocus
            />
            <p style={styles.editModalLabel}>Review 直达链接（选填，有此链接则优先使用）</p>
            <input
              style={styles.editModalInput}
              value={editingMerchant.googleReviewUrl}
              onChange={(e) => setEditingMerchant(u => ({ ...u, googleReviewUrl: e.target.value }))}
              placeholder="g.page/r/.../review（从 Google Business Profile 获取）"
            />
            <p style={styles.editModalLabel}>博主探店余额（还剩多少个博主名额，选人时扣减）</p>
            <input
              style={styles.editModalInput}
              type="tel"
              inputMode="numeric"
              value={editingMerchant.tandianBalance}
              onChange={(e) => setEditingMerchant(u => ({ ...u, tandianBalance: e.target.value.replace(/\D/g, '') }))}
              placeholder="0"
            />
            <p style={styles.editModalLabel}>
              营业时间（选填；助理问接待时段时拿它当默认）
              {editingMerchant.businessHoursSource === 'google' ? '　当前是 Google 上查到的' : ''}
            </p>
            <input
              style={styles.editModalInput}
              value={editingMerchant.businessHoursText}
              onChange={(e) => setEditingMerchant(u => ({ ...u, businessHoursText: e.target.value, businessHoursSource: 'ops', hoursTouched: true }))}
              placeholder="周一到周五 11:00-21:00，周六 10:00-22:00"
            />
            <button
              style={styles.hoursBtn}
              onClick={async () => {
                try {
                  // 只查不存：查到的填进输入框，点「保存」才落库 —— 不对就取消，原来的一个字不动
                  const r = await agentApi.previewHours(editingMerchant.id);
                  setEditingMerchant(u => ({ ...u, businessHoursText: r.text, businessHoursSource: 'google', hoursTouched: true }));
                  alert(`查到了：${r.matched}`);
                } catch (err) {
                  alert(err?.response?.data?.error || '查询失败');
                }
              }}
            >
              从 Google 查
            </button>
            <label style={styles.urgentRow}>
              <input type="checkbox" checked={!!editingMerchant.tandianUrgent} onChange={(e) => setEditingMerchant(u => ({ ...u, tandianUrgent: e.target.checked }))} />
              <span>标记「加急找博主」（商家列表置顶 + 显示急标记）</span>
            </label>
            <p style={styles.editModalHint}>保存后自动同步该商家旗下所有活动。</p>
            <div style={styles.editModalBtns}>
              <button style={styles.cancelBtn} onClick={() => setEditingMerchant(null)}>取消</button>
              <button style={styles.saveBtn} onClick={handleSaveMerchant} disabled={savingMerchant}>
                {savingMerchant ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSearch && (
        <div style={styles.overlay} onClick={() => { setShowSearch(false); setSearchName(''); setSearchResults([]); }}>
          <div style={styles.drawer} onClick={(e) => e.stopPropagation()}>
            <div style={styles.drawerHandle} />
            <div style={styles.drawerHeader}>
              <span style={styles.drawerTitle}>搜索券宝宝商家</span>
              <button style={styles.closeBtn} onClick={() => { setShowSearch(false); setSearchName(''); setSearchResults([]); }}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M4.5 13.5L13.5 4.5M4.5 4.5L13.5 13.5" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <div style={styles.searchWrap}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={styles.searchIcon}>
                <circle cx="7" cy="7" r="4.5" stroke="#9ca3af" strokeWidth="1.5"/>
                <path d="M11 11L13.5 13.5" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <input
                style={styles.searchInput}
                placeholder="输入商家名称..."
                value={searchName}
                onChange={(e) => handleSearchInput(e.target.value)}
                autoFocus
              />
            </div>
            <div style={styles.results}>
              {searching && (
                <div style={styles.searchingText}>搜索中...</div>
              )}
              {!searching && searchName && searchResults.length === 0 && (
                <div style={styles.noResult}>未找到相关商家</div>
              )}
              {searchResults.map((s) => {
                const sid = String(s.store_id);
                const alreadyAdded = merchants.some(m => m.externalId === sid);
                return (
                  <div key={sid} style={styles.resultRow}>
                                      <div style={styles.resultAvatar}>{s.store_name?.charAt(0) || '?'}</div>
                    <div style={styles.resultInfo}>
                      <div style={styles.resultName}>{s.store_name}</div>
                      {s.store_address && <div style={styles.resultSub}>{s.store_address}</div>}
                    </div>
                    <button
                      style={alreadyAdded ? styles.addedBtn : styles.addBtn}
                      disabled={alreadyAdded || adding === sid}
                      onClick={() => handleAdd(s)}
                    >
                      {alreadyAdded ? '已添加' : adding === sid ? '...' : '添加'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {qr && <QrModal url={qr.url} title={qr.title} onClose={() => setQr(null)} />}
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#f7f7f8', display: 'flex', flexDirection: 'column' },
  body: { flex: 1, padding: '12px 12px 24px' },
  loading: { display: 'flex', flexDirection: 'column', gap: 10 },
  skeleton: { height: 72, borderRadius: 12, background: '#efefef' },
  list: { display: 'flex', flexDirection: 'column', gap: 10 },
  card: {
    background: '#fff', borderRadius: 12, padding: '14px 16px',
    display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
    boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
  },
  avatar: {
    width: 44, height: 44, borderRadius: 12, background: '#FA1C51',
    color: '#fff', fontSize: 18, fontWeight: 700,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  info: { flex: 1, minWidth: 0 },
  name: { fontSize: 16, fontWeight: 600, color: '#111', marginBottom: 3 },
  idTag: {
    display: 'inline-block', fontSize: 11, color: '#FA1C51', background: '#fff0f3',
    padding: '1px 6px', borderRadius: 4, fontWeight: 600, marginBottom: 3,
  },
  sub: { fontSize: 12, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  scanRow: { fontSize: 12, color: '#6b7280', marginTop: 3 },
  scanNum: { color: '#FA1C51', fontWeight: 700 },
  googleRow: { display: 'flex', alignItems: 'center', gap: 4, marginTop: 3 },

  statsStrip: {
    background: '#fff', borderRadius: 12, padding: '12px 16px', marginBottom: 10,
    display: 'flex', alignItems: 'center', cursor: 'pointer',
    boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
  },
  statsItem: { flex: 1, textAlign: 'center' },
  statsNum: { fontSize: 18, fontWeight: 700, color: '#111', lineHeight: 1.2 },
  statsLabel: { fontSize: 11, color: '#9ca3af', marginTop: 2 },
  statsLink: { fontSize: 12, color: '#FA1C51', fontWeight: 600, flexShrink: 0, marginLeft: 8 },
  editBtn: {
    width: 32, height: 32, border: '1px solid #e5e7eb', borderRadius: 8,
    background: '#fff', cursor: 'pointer', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  editModal: {
    background: '#fff', borderRadius: 14, padding: 20, margin: 20,
  },
  editModalTitle: { fontSize: 15, fontWeight: 700, color: '#111', margin: '0 0 12px' },
  editModalLabel: { fontSize: 12, color: '#6b7280', margin: '0 0 5px', fontWeight: 500 },
  editModalInput: {
    width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb',
    borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', marginBottom: 6,
  },
  editModalHint: { fontSize: 11, color: '#9ca3af', margin: '0 0 14px' },
  hoursBtn: { fontSize: 12, color: '#6b7280', background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 10px', margin: '0 0 12px', cursor: 'pointer' },
  urgentTag: { display: 'inline-block', fontSize: 11, color: '#fff', background: '#ef4444', borderRadius: 4, padding: '0 5px', marginRight: 6, fontWeight: 700, verticalAlign: 'middle' },
  urgentRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', margin: '0 0 12px' },
  editModalBtns: { display: 'flex', gap: 8, justifyContent: 'flex-end' },
  cancelBtn: {
    padding: '9px 18px', border: '1px solid #e5e7eb', borderRadius: 8,
    background: '#fff', color: '#374151', cursor: 'pointer', fontSize: 14,
  },
  saveBtn: {
    padding: '9px 18px', border: 'none', borderRadius: 8,
    background: '#FA1C51', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600,
  },

  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
    display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', zIndex: 100,
  },
  drawer: {
    background: '#fff', borderRadius: '20px 20px 0 0', padding: '8px 16px 32px',
    maxHeight: '80vh', display: 'flex', flexDirection: 'column',
  },
  drawerHandle: {
    width: 36, height: 4, borderRadius: 2, background: '#e5e7eb',
    margin: '6px auto 16px',
  },
  drawerHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  drawerTitle: { fontSize: 16, fontWeight: 700, color: '#111' },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' },
  searchWrap: {
    display: 'flex', alignItems: 'center', gap: 8, background: '#f5f5f5',
    borderRadius: 10, padding: '0 12px', marginBottom: 12,
  },
  searchIcon: { flexShrink: 0 },
  searchInput: {
    flex: 1, padding: '11px 0', border: 'none', background: 'transparent',
    fontSize: 15, outline: 'none', color: '#111',
  },
  results: { overflowY: 'auto', flex: 1 },
  searchingText: { textAlign: 'center', color: '#9ca3af', fontSize: 14, padding: '20px 0' },
  noResult: { textAlign: 'center', color: '#9ca3af', fontSize: 14, padding: '20px 0' },
  resultRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid #f5f5f5' },
  resultAvatar: {
    width: 36, height: 36, borderRadius: 9, background: '#f3f4f6',
    color: '#374151', fontSize: 15, fontWeight: 700,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  resultInfo: { flex: 1, minWidth: 0 },
  resultName: { fontSize: 14, fontWeight: 600, color: '#111' },
  resultSub: { fontSize: 12, color: '#9ca3af', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  addBtn: {
    padding: '6px 14px', background: '#FA1C51', color: '#fff',
    border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600, flexShrink: 0,
  },
  addedBtn: {
    padding: '6px 14px', background: '#f3f4f6', color: '#9ca3af',
    border: 'none', borderRadius: 7, cursor: 'default', fontSize: 13, flexShrink: 0,
  },
};
